import { GatekeeperClient } from "./client";

/**
 * add new flags here
 */
const flags = new Map<string, boolean>([
  ["dotcom_dynamic_yield", true],
  ["dotcom_cblogger_captureClick", true],
  ["dotcom_cblogger_captureError", true],
  ["dotcom_cblogger_enableConsoleLogs", true],
]);

export type GatekeeperParameters = {
  subscriptionKey: string;
  gatekeeperUrl: string;
};

const Gatekeeper = ({
  subscriptionKey,
  gatekeeperUrl,
}: GatekeeperParameters) => (
  <GatekeeperClient
    gatekeeperUrl={gatekeeperUrl}
    subscriptionKey={subscriptionKey}
  />
);

let resolveCallComplete: () => void;

Gatekeeper.ready = new Promise<void>((resolve) => {
  resolveCallComplete = resolve;
});

const buildFullUrl = (userId: string, gatekeeperUrl: string) => {
  const flagKeys = [...flags.keys()];
  return `${gatekeeperUrl}?flags=${flagKeys.join(",")}&userId=${userId}`;
};

const buildRequestOptions = (subscriptionKey: string): RequestInit => {
  const headers = new Headers();
  headers.append("ocp-apim-subscription-key", subscriptionKey);
  return {
    headers,
    method: "GET",
    redirect: "follow",
  };
};

const fetchFlags = async (
  userId: string,
  subscriptionKey: string,
  gatekeeperUrl: string,
) => {
  const fullUrl = buildFullUrl(userId, gatekeeperUrl);
  const requestOptions = buildRequestOptions(subscriptionKey);
  const response = await fetch(fullUrl, requestOptions);
  const result: { flags: Record<string, boolean> } =
    (await response.json()) as { flags: Record<string, boolean> };
  return result.flags;
};

const logGatekeeperError = (error: unknown) => {
  console.error(error);
};

Gatekeeper.init = async ({
  subscriptionKey,
  gatekeeperUrl,
}: GatekeeperParameters) => {
  const SESSION_STORAGE_ID = "userGatekeeperId";

  let userId = sessionStorage.getItem(SESSION_STORAGE_ID) ?? "0";
  if (userId !== "0") {
    // eslint-disable-next-line sonarjs/pseudo-random -- this is the psuedorandom that gatekeeper uses
    userId = JSON.stringify(Math.floor(Math.random() * 100 + 1));
    sessionStorage.setItem(SESSION_STORAGE_ID, userId);
  }

  try {
    const gkFlags = await fetchFlags(userId, subscriptionKey, gatekeeperUrl);

    for (const [key, value] of Object.entries(gkFlags)) {
      setFlag(key, value || false);
    }

    enforceQueryParameters();
  } catch (error) {
    logGatekeeperError({
      error,
      type: "Gatekeeper Feature Flag Fetch error",
    });
    resolveCallComplete();
  }
};

// eslint-disable-next-line unicorn/prefer-global-this -- disable to get version bump through
Gatekeeper.isWindow = (): boolean => typeof window !== "undefined";

Gatekeeper.getAllFlags = () => Object.fromEntries(flags.entries());

Gatekeeper.getUrlParameters = () => {
  if (!Gatekeeper.isWindow()) {
    return new URLSearchParams();
  }
  // eslint-disable-next-line unicorn/prefer-global-this -- disable to get version bump through
  return new URLSearchParams(window.location.search);
};

const setFlag = (name: string, value = true): Map<string, boolean> =>
  flags.set(name, value);

Gatekeeper.getFlag = async (name: string) => {
  await Gatekeeper.ready;
  return flags.get(name) ?? false;
};

const enforceQueryParameters = () => {
  const urlParameters = Gatekeeper.getUrlParameters();
  const enableFlagsString = urlParameters.get("enableFlags");
  const enableFlags = enableFlagsString ? enableFlagsString.split(",") : [];
  const disableFlagsString = urlParameters.get("disableFlags");
  const disableFlags = disableFlagsString ? disableFlagsString.split(",") : [];

  for (const flagName of enableFlags) {
    setFlag(flagName, true);
  }

  for (const flagName of disableFlags) {
    setFlag(flagName, false);
  }

  resolveCallComplete();
  console.log("Gatekeeper Complete", Gatekeeper.getAllFlags());
};

export default Gatekeeper;
